import { Lightbulb, Plus } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { UseCaseDetail } from '../../../types'; // Importer le type UseCaseDetail
 // Importer les icônes
import PersonaDisplaySection from '../../UseCases/PersonaDisplaySection'; // Pour afficher les personas
import MarkdownDisplay from '../../Common/MarkdownDisplay';
import { useUseCasePersonas } from '../../../hooks/useUseCasePersonas';
import { useTranslation } from 'react-i18next';

interface CadrageLinkedUseCaseProps {
  linkedUseCase: UseCaseDetail | null | undefined; // Le cas d'usage lié depuis le store
  isLoading: boolean; // Pour désactiver le bouton pendant le chargement
  onCreateClick: () => void; // Callback pour ouvrir la modale de création
  isReadOnly?: boolean; // Mode lecture seule
}

const CadrageLinkedUseCaseSection: React.FC<CadrageLinkedUseCaseProps> = ({
  linkedUseCase,
  isLoading,
  onCreateClick,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  // Charger les personas associés via l'API (avec avatar_url) plutôt que d'utiliser directement linkedUseCase.personas
  const {
    associatedPersonas,
    isLoading: isLoadingPersonas,
    error: errorPersonas,
  } = useUseCasePersonas(linkedUseCase?.id);

  return (
    <div className="bg-card rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-foreground flex items-center">
          <Lightbulb className="h-5 w-5 mr-2 text-primary" />
          {t('cadrage.linkedUseCase.title', 'Cas d\'Usage Associé')}
        </h2>
        {/* Afficher le bouton Créer seulement s'il n'y a pas déjà un cas d'usage lié et si pas en chargement */}
        {!linkedUseCase && !isLoading && (
          <button
            onClick={onCreateClick}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90"
          >
            <Plus className="h-4 w-4 mr-1" />
            {t('cadrage.linkedUseCase.createButton', 'Créer un Cas d\'Usage')}
          </button>
        )}
      </div>

      {isLoading && <p className="text-muted-foreground italic">{t('global.loading', 'Chargement...')}</p>}

      {!isLoading && linkedUseCase ? (
        <div>
          <p className="text-sm font-medium text-muted-foreground">{t('cadrage.linkedUseCase.nameLabel', 'Nom du Cas d\'Usage')}</p>
          <p className="text-sm text-primary hover:underline">
            {/* Utiliser le chemin correct pour la page de détail du cas d'usage */}
            <Link to={`/cas-usage/${linkedUseCase.id}`}>
              {linkedUseCase.name}
            </Link>
          </p>
          <p className="mt-2 text-sm text-foreground">
            {linkedUseCase.short_description || (
              <span className="italic text-muted-foreground">{t('cadrage.linkedUseCase.noShortDescription', 'Pas de description courte.')}</span>
            )}
          </p>
          {/* Description détaillée issue automatiquement du Use Case associé */}
          <div className="mt-4 space-y-3">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('cadrage.linkedUseCase.detailedDescription', 'Description détaillée')}</p>
              <MarkdownDisplay 
                content={linkedUseCase.description} 
                emptyMessage={t('cadrage.linkedUseCase.noDetailedDescription', 'Aucune description détaillée renseignée.')} 
              />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('cadrage.linkedUseCase.businessNeed', 'Besoin métier')}</p>
              <MarkdownDisplay 
                content={linkedUseCase.business_need} 
                emptyMessage={t('cadrage.linkedUseCase.notSpecified', 'Non renseigné.')} 
              />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('cadrage.linkedUseCase.technicalElements', 'Éléments techniques')}</p>
              <MarkdownDisplay 
                content={linkedUseCase.technical_elements} 
                emptyMessage={t('cadrage.linkedUseCase.notSpecified', 'Non renseigné.')} 
              />
            </div>
          </div>
          {/* Affichage des scores de qualification */}
          <div className="mt-3 text-xs text-muted-foreground space-y-1">
            <p>{t('cadrage.linkedUseCase.value', 'Valeur:')} <span className="font-medium text-foreground">{linkedUseCase.value_score ?? '-'}</span></p>
            <p>{t('cadrage.linkedUseCase.complexity', 'Complexité:')} <span className="font-medium text-foreground">{linkedUseCase.complexity_score ?? '-'}</span></p>
            {/* Ajouter d'autres scores si nécessaire */}
          </div>
          {/* Affichage des personas */}
          <div className="mt-4">
             <h4 className="text-sm font-medium text-foreground mb-2">{t('cadrage.linkedUseCase.associatedPersonas', 'Personas Associés')}</h4>
             {isLoadingPersonas && (
               <p className="text-muted-foreground italic text-sm">{t('cadrage.linkedUseCase.loadingPersonas', 'Chargement des personas...')}</p>
             )}
             {errorPersonas && !isLoadingPersonas && (
               <p className="text-red-500 italic text-sm">{t('cadrage.linkedUseCase.errorLoadingPersonas', 'Erreur lors du chargement des personas:')} {errorPersonas}</p>
             )}
             {!isLoadingPersonas && !errorPersonas && (
               associatedPersonas && associatedPersonas.length > 0 ? (
                 <PersonaDisplaySection personas={associatedPersonas} readOnly={true} />
               ) : (
                 <p className="text-muted-foreground italic text-sm">{t('cadrage.linkedUseCase.noPersonas', 'Aucun persona associé.')}</p>
               )
             )}
          </div>
        </div>
      ) : (
        !isLoading && <p className="text-muted-foreground italic">{t('cadrage.linkedUseCase.empty', 'Aucun cas d\'usage directement lié à ce cadrage pour le moment. Vous pouvez en créer un.')}</p>
      )}
    </div>
  );
};

export default CadrageLinkedUseCaseSection;
